import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import Card from '../common/Card';
import LoadingSpinner from '../common/LoadingSpinner';
import Icon from '../common/Icon';
import { studiesAPI } from '../../services/api';
import { formatDate, formatNumber } from '../../utils/formatters';
import { getCssVar } from '../../utils/cssVar';
import './ComparisonView.css';

const DeltaValue = ({ value, format = 'number', inverse = false }) => {
  if (value === null || value === undefined) return <span className="delta-na">-</span>;
  
  const isPositive = inverse ? value < 0 : value > 0;
  const isNegative = inverse ? value > 0 : value < 0;
  
  let displayValue;
  if (format === 'percent') {
    displayValue = `${value >= 0 ? '+' : ''}${value.toFixed(2)}%`;
  } else if (format === 'pts') {
    displayValue = `${value >= 0 ? '+' : ''}${value.toFixed(2)} pts`;
  } else {
    displayValue = `${value >= 0 ? '+' : ''}${formatNumber(value)}`;
  }

  return (
    <span className={`delta-value ${isPositive ? 'positive' : ''} ${isNegative ? 'negative' : ''}`}>
      {isPositive && <Icon name="TrendingUp" size={14} />}
      {isNegative && <Icon name="TrendingDown" size={14} />}
      {displayValue}
    </span>
  );
};

const ComparisonView = ({ studyId }) => {
  const [snapshots, setSnapshots] = useState(null);
  const [comparison, setComparison] = useState(null);
  const [loading, setLoading] = useState(true);
  const [comparing, setComparing] = useState(false);
  const [error, setError] = useState(null);
  const [selectedA, setSelectedA] = useState(null);
  const [selectedB, setSelectedB] = useState(null);

  useEffect(() => {
    const fetchSnapshots = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await studiesAPI.getSnapshots(studyId);
        setSnapshots(response);
        
        const completedSnapshots = response.snapshots?.filter(s => s.status === 'Completed') || [];
        if (completedSnapshots.length >= 2) {
          const sorted = [...completedSnapshots].sort((a, b) => 
            new Date(b.createdAt) - new Date(a.createdAt)
          );
          setSelectedB(sorted[0].studyId);
          setSelectedA(sorted[1].studyId);
        } else if (completedSnapshots.length === 1) {
          setSelectedA(completedSnapshots[0].studyId);
          setSelectedB(completedSnapshots[0].studyId);
        }
      } catch (err) {
        console.error('Error fetching snapshots:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchSnapshots();
  }, [studyId]);

  useEffect(() => {
    const fetchComparison = async () => {
      if (!selectedA || !selectedB || selectedA === selectedB) {
        setComparison(null);
        return;
      }

      try {
        setComparing(true);
        const response = await studiesAPI.compare(selectedA, selectedB);
        setComparison(response);
      } catch (err) {
        console.error('Error comparing studies:', err);
        setError(err.message);
      } finally {
        setComparing(false);
      }
    };

    fetchComparison();
  }, [selectedA, selectedB]);

  const completedSnapshots = useMemo(() => {
    if (!snapshots?.snapshots) return [];
    return snapshots.snapshots
      .filter(s => s.status === 'Completed')
      .sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
  }, [snapshots]);

  const positionChartData = useMemo(() => {
    if (!comparison?.positionDistributionComparison) return [];
    return comparison.positionDistributionComparison.map(item => ({
      ...item,
      name: item.range,
    }));
  }, [comparison]);

  const handleSwap = useCallback(() => {
    setSelectedA(selectedB);
    setSelectedB(selectedA);
  }, [selectedA, selectedB]);

  if (loading) {
    return (
      <Card title="Comparaison">
        <div className="comparison-loading">
          <LoadingSpinner size="md" text="Chargement des données..." />
        </div>
      </Card>
    );
  }

  if (error) {
    return (
      <Card title="Comparaison">
        <div className="comparison-error">
          <Icon name="AlertCircle" size={24} color="var(--color-error)" />
          <p>{error}</p>
        </div>
      </Card>
    );
  }

  if (completedSnapshots.length < 2) {
    return (
      <Card title="Comparaison">
        <div className="comparison-empty">
          <Icon name="GitCompare" size={48} color="var(--color-gray-400)" />
          <p>Impossible de comparer</p>
          <p className="comparison-empty-hint">
            Vous avez besoin d'au moins 2 analyses terminées pour effectuer une comparaison.
            Relancez l'étude depuis l'onglet Évolution pour créer un nouveau snapshot.
          </p>
        </div>
      </Card>
    );
  }

  const studyA = comparison?.studyA;
  const studyB = comparison?.studyB;
  const evolution = comparison?.evolution;

  return (
    <div className="comparison-container">
      <Card title="Sélection des études à comparer">
        <div className="comparison-selectors">
          <div className="selector-group">
            <label>Étude de référence (avant)</label>
            <select 
              value={selectedA || ''} 
              onChange={(e) => setSelectedA(e.target.value)}
            >
              {completedSnapshots.map(s => (
                <option key={s.studyId} value={s.studyId}>
                  #{s.snapshotNumber} - {formatDate(s.createdAt)}
                </option>
              ))}
            </select>
          </div>

          <button className="swap-btn" onClick={handleSwap} title="Inverser">
            <Icon name="ArrowLeftRight" size={20} />
          </button>

          <div className="selector-group">
            <label>Étude à comparer (après)</label>
            <select 
              value={selectedB || ''} 
              onChange={(e) => setSelectedB(e.target.value)}
            >
              {completedSnapshots.map(s => (
                <option key={s.studyId} value={s.studyId}>
                  #{s.snapshotNumber} - {formatDate(s.createdAt)}
                </option>
              ))}
            </select>
          </div>
        </div>

        {selectedA === selectedB && (
          <div className="comparison-warning">
            <Icon name="AlertTriangle" size={16} />
            Sélectionnez deux études différentes pour les comparer.
          </div>
        )}
      </Card>

      {comparing ? (
        <Card title="Résultats">
          <div className="comparison-loading">
            <LoadingSpinner size="md" text="Comparaison en cours..." />
          </div>
        </Card>
      ) : comparison && selectedA !== selectedB ? (
        <>
          <Card title="Comparaison des KPIs">
            <div className="kpi-comparison-table">
              <table>
                <thead>
                  <tr>
                    <th>KPI</th>
                    <th>#{studyA?.snapshotNumber} ({formatDate(studyA?.createdAt)})</th>
                    <th>#{studyB?.snapshotNumber} ({formatDate(studyB?.createdAt)})</th>
                    <th>Évolution</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className="kpi-name">
                      <Icon name="PieChart" size={16} />
                      Part de Voix
                    </td>
                    <td>{studyA?.shareOfVoice.toFixed(2)}%</td>
                    <td>{studyB?.shareOfVoice.toFixed(2)}%</td>
                    <td>
                      <DeltaValue value={evolution?.shareOfVoiceDelta} format="pts" />
                      <span className="delta-percent">
                        (<DeltaValue value={evolution?.shareOfVoicePercent} format="percent" />)
                      </span>
                    </td>
                  </tr>
                  <tr>
                    <td className="kpi-name">
                      <Icon name="TrendingUp" size={16} />
                      Trafic Estimé
                    </td>
                    <td>{formatNumber(studyA?.estimatedTraffic)}</td>
                    <td>{formatNumber(studyB?.estimatedTraffic)}</td>
                    <td>
                      <DeltaValue value={evolution?.estimatedTrafficDelta} />
                      <span className="delta-percent">
                        (<DeltaValue value={evolution?.estimatedTrafficPercent} format="percent" />)
                      </span>
                    </td>
                  </tr>
                  <tr>
                    <td className="kpi-name">
                      <Icon name="Eye" size={16} />
                      Taux de Présence
                    </td>
                    <td>{studyA?.presenceRate.toFixed(2)}%</td>
                    <td>{studyB?.presenceRate.toFixed(2)}%</td>
                    <td>
                      <DeltaValue value={evolution?.presenceRateDelta} format="pts" />
                      <span className="delta-percent">
                        (<DeltaValue value={evolution?.presenceRatePercent} format="percent" />)
                      </span>
                    </td>
                  </tr>
                  <tr>
                    <td className="kpi-name">
                      <Icon name="Award" size={16} />
                      Top 10
                    </td>
                    <td>{studyA?.rank1To3Count + studyA?.rank4To5Count + studyA?.rank6To10Count}</td>
                    <td>{studyB?.rank1To3Count + studyB?.rank4To5Count + studyB?.rank6To10Count}</td>
                    <td>
                      <DeltaValue value={evolution?.top10Delta} />
                      <span className="delta-percent">
                        (<DeltaValue value={evolution?.top10Percent} format="percent" />)
                      </span>
                    </td>
                  </tr>
                  <tr>
                    <td className="kpi-name">
                      <Icon name="Target" size={16} />
                      Position Moyenne
                    </td>
                    <td>{studyA?.averagePosition?.toFixed(1) || '-'}</td>
                    <td>{studyB?.averagePosition?.toFixed(1) || '-'}</td>
                    <td>
                      <DeltaValue value={evolution?.averagePositionDelta} inverse />
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </Card>

          <Card title="Distribution des positions">
            <div className="position-comparison">
              <div className="position-chart">
                <ResponsiveContainer width="100%" height={250}>
                  <BarChart 
                    data={positionChartData} 
                    layout="vertical"
                    margin={{ top: 10, right: 30, left: 50, bottom: 10 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" horizontal={true} vertical={false} />
                    <XAxis type="number" />
                    <YAxis dataKey="name" type="category" tick={{ fontSize: 12 }} />
                    <Tooltip 
                      formatter={(value, name) => [value, name]}
                    />
                    <Bar dataKey="countA" name="Avant" fill={getCssVar('--color-gray-400')} radius={[0, 4, 4, 0]} />
                    <Bar dataKey="countB" name="Après" fill={getCssVar('--chart-primary-1')} radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>

              <div className="position-table">
                <table>
                  <thead>
                    <tr>
                      <th>Position</th>
                      <th>Avant</th>
                      <th>Après</th>
                      <th>Delta</th>
                    </tr>
                  </thead>
                  <tbody>
                    {positionChartData.map(row => (
                      <tr key={row.range}>
                        <td className="range-label">{row.range}</td>
                        <td>{row.countA}</td>
                        <td>{row.countB}</td>
                        <td>
                          <DeltaValue value={row.delta} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </Card>
        </>
      ) : null}
    </div>
  );
};

export default ComparisonView;
